/**
 * The customer's view of a quote — `docs/proposal.md` §4, §7.
 *
 * **The customer's browser gets a `ProposalView`, never a `QuoteTree`**
 * (§2 rule 1). The tree carries `cost`, `hours`, `grossMargin`, `marginPct`,
 * `byDivision`, every line's `cost` and `standardRate`, and each visit's
 * `noteInternal` and `noteCrew`; the portal used to receive all of it and
 * simply not draw it, so anyone who opened a network tab read the margin on
 * their own quote. The projection that builds this lives on the server
 * (`services/proposal.ts`), is written once, and has a test that fails if a
 * forbidden key appears anywhere in the JSON.
 *
 * **The server writes every number; the client adds, never prices** (rule 2).
 * The only arithmetic the customer's browser does is `selectionTotals()` and
 * `monthSplit()` — pure functions here that sum amounts the server already
 * decided — and the server re-runs the same functions, and `recalcQuote`, on
 * every price and approve call. A figure the client invented is a figure that
 * can disagree with the invoice.
 *
 * Everything in this file is pure: no imports from the server, no dates read
 * from a clock, no money computed from a rate. That is what lets one function
 * answer the same question in three places — the page as somebody taps, the
 * `price` endpoint, and `approve`'s final check.
 */
import { z } from 'zod';
export const PROPOSAL_DETAILS = ['visits', 'services', 'lines'];
export const PROPOSAL_KINDS = ['material', 'labor', 'equipment', 'fee'];
export const PRICE_VIEWS = ['plan', 'visit', 'both'];
export const TIMING_MODES = ['count', 'frequency', 'season', 'dates'];
/**
 * Fees are **off** by default.
 *
 * To a customer a fee is an upcharge, and a line called "Disposal fee" beside
 * the work it belongs to invites a conversation about the fee rather than
 * about the work. Its amount stays inside the service's price either way
 * (`hiddenEach`), so turning a kind off never changes what anything costs —
 * only what is listed. Decision 16.
 */
export const DEFAULT_PROPOSAL_KINDS = ['material', 'labor', 'equipment'];
/**
 * The sentence under a visit when a company has written none of its own.
 *
 * **It asserts no process**, deliberately (§2 rule 12): no weekday, no
 * day-before text, no start date, no promise about how soon. Every one of
 * those is a thing some companies do and others do not, and a default that
 * guesses is a default that puts a promise in a customer's hands that nobody
 * at the company made. The phrase library (`proposal_phrases`) is where a
 * company says what it actually does, and migration 0114 seeds five.
 */
export const DEFAULT_TIMING_PHRASE = 'We\u2019ll contact you with your schedule once you accept.';
/**
 * The five phrases a company starts with (board 15 of `43-customer-proposal`).
 *
 * They are **rows, not constants**, the moment a tenant exists: a company
 * renames them, reorders them, rewrites them and deletes the ones that do not
 * describe how it works. These are only the starting point, and the first is
 * the default — the sentence a visit says when nobody has chosen one.
 *
 * Every creation path inserts from here: `signUp()`, `seedTenant()` and
 * migration `0114` for the tenants that already existed. The migration's SQL
 * is the one unavoidable second copy, the same situation `tenant-slug`'s CHECK
 * is in; `tests/migration-data.test.ts` fails if the two ever disagree.
 */
export const SEEDED_PHRASES = [
    { name: 'After you accept', text: DEFAULT_TIMING_PHRASE },
    {
        name: 'One visit, we confirm the day',
        text: 'We\u2019ll confirm the day with you after you accept.',
    },
    {
        name: 'Before the freeze',
        text: 'We\u2019ll book it before the first hard freeze.',
    },
    {
        name: 'Depends on the weather',
        text: 'We schedule around the weather and call you the day before.',
    },
    {
        name: 'Multi-day project',
        text: 'About 2 to 3 working days. We\u2019ll confirm the dates with you.',
    },
];
// ---------------------------------------------------------------------------
// "Everything in Silver, plus …" (§7.3)
// ---------------------------------------------------------------------------
/**
 * Which option each option builds on, worked out from the visit sets.
 *
 * **It is computed, never typed.** An estimator who moves one visit between
 * plans would otherwise have to remember to edit a sentence on a card, and the
 * sentence would be wrong until they did — on the customer's page, about the
 * thing the customer is comparing. So: option B *inherits* option A when A's
 * visits are a **strict subset** of B's, and where several qualify the one
 * with the most visits wins, because that is the shortest true sentence.
 *
 * When the sets merely overlap — A has a trip B lacks and B has one A lacks —
 * there is no "everything in", and B lists all of its own visits. Two plans
 * that are genuinely different are not a ladder, and drawing them as one would
 * hide the trip the customer is choosing between.
 *
 * Ties by visit count are broken by the order the options are given in, which
 * is `sort_order`: the office's own left-to-right reading of its plans.
 */
export function inheritsFrom(options) {
    const sets = new Map(options.map((one) => [one.id, new Set(one.visitIds)]));
    const found = new Map();
    for (const option of options) {
        const mine = sets.get(option.id);
        let best = null;
        for (const other of options) {
            if (other.id === option.id)
                continue;
            const theirs = sets.get(other.id);
            /* Strict: the same set twice is two names for one plan, not a ladder. */
            if (theirs.size === 0 || theirs.size >= mine.size)
                continue;
            let inside = true;
            for (const visitId of theirs)
                if (!mine.has(visitId)) {
                    inside = false;
                    break;
                }
            if (!inside)
                continue;
            if (!best || theirs.size > best.size) {
                best = { id: other.id, name: other.name, size: theirs.size };
            }
        }
        if (best)
            found.set(option.id, { optionId: best.id, name: best.name });
    }
    return found;
}
/** What an option adds over the one it inherits from, in the option's order. */
export function addsOver(visitIds, inherited) {
    if (!inherited)
        return [...visitIds];
    const had = new Set(inherited);
    return visitIds.filter((id) => !had.has(id));
}
export const EMPTY_SELECTION = { choose: {}, visits: [], services: [], lines: [] };
/**
 * What the page opens with.
 *
 * **Nothing optional is on at the start**, and a group is preselected only
 * where somebody marked an option **Recommended**. The as-built page
 * preselected the first visit in every group, which is a nudge the office
 * never typed — and the one it would have typed is exactly what Recommended
 * is for. With no recommendation the customer must choose, and the sign card
 * says what is missing (decision 4).
 */
export function initialSelection(view) {
    const choose = {};
    for (const group of view.groups) {
        const picked = group.options.find((one) => one.recommended);
        if (picked)
            choose[group.id] = picked.id;
    }
    return { choose, visits: [], services: [], lines: [] };
}
// ---------------------------------------------------------------------------
// What counts, and what it comes to (§7.2)
// ---------------------------------------------------------------------------
/** Money is summed in integer cents and rounded once, at the end of a visit. */
const cents = (amount) => Math.round(amount * 100);
const money = (inCents) => Math.round(inCents) / 100;
/**
 * Is this visit part of what the customer is buying?
 *
 * A visit in several options of one group is included **once** — that is the
 * whole point of plans that share visits (§7.3).
 */
export function visitIncluded(visit, sel) {
    if (visit.kind === 'included')
        return true;
    if (visit.kind === 'optional')
        return sel.visits.includes(visit.id);
    const chosen = sel.choose[visit.groupId ?? ''];
    return chosen !== undefined && chosen !== null && visit.optionIds.includes(chosen);
}
const serviceIncluded = (service, sel) => !service.optional || sel.services.includes(service.id);
const lineIncluded = (line, sel) => !line.optional || sel.lines.includes(line.id);
/**
 * What the selection comes to.
 *
 * The one piece of arithmetic the customer's browser does, and the server
 * runs the same function on the same view before it writes anything — so a
 * disagreement is a bug in this file rather than a difference of opinion
 * between two implementations.
 *
 * Open-ended visits contribute to `recurring`, not to the subtotal: there is
 * no number of trips to multiply by.
 */
export function selectionTotals(view, sel) {
    let subtotalCents = 0;
    let taxableCents = 0;
    const byVisit = {};
    for (const visit of view.visits) {
        if (!visitIncluded(visit, sel))
            continue;
        if (visit.count.openEnded) {
            byVisit[visit.id] = 0;
            continue;
        }
        let eachCents = 0;
        let eachTaxCents = 0;
        for (const service of visit.services) {
            if (!serviceIncluded(service, sel))
                continue;
            eachCents += cents(service.hiddenEach);
            eachTaxCents += cents(service.hiddenTaxableEach);
            for (const line of service.lines) {
                if (!lineIncluded(line, sel))
                    continue;
                eachCents += cents(line.amountEach);
                if (line.taxable)
                    eachTaxCents += cents(line.amountEach);
            }
        }
        const occurrences = Math.max(0, visit.price.occurrences);
        const visitCents = eachCents * occurrences;
        subtotalCents += visitCents;
        taxableCents += eachTaxCents * occurrences;
        byVisit[visit.id] = money(visitCents);
    }
    const subtotal = money(subtotalCents);
    const taxable = money(taxableCents);
    const taxCents = Math.round(taxableCents * view.tax.rate);
    const tax = money(taxCents);
    // Added in cents: $1,322.60 + $24.05 in floats is $1,346.6499999999999.
    return { subtotal, taxable, tax, total: money(subtotalCents + taxCents), byVisit };
}
/**
 * A visit's price said again for a different total — the same plan, the same
 * number of trips, the money moved.
 *
 * The server prices a visit as what it must buy, so a customer who ticks an
 * optional line or service on it saw the total card move and the visit's own
 * figure stay where it was — "$288 a month" over work that had just become
 * $300 a month. `selectionTotals().byVisit` is the visit's money with the
 * ticks in it, and this re-says that money in the visit's own terms. Every
 * figure the server writes is proportional to the total (a period's share, a
 * trip's share, or the total itself), so scaling keeps the plan's shape; a
 * visit priced at nought has no proportion to keep and is worked out from
 * the plan instead.
 */
export function repriced(price, total) {
    if (Math.abs(total - price.total) < 0.005)
        return price;
    const perVisit = money(cents(total / Math.max(1, price.occurrences)));
    const figure = price.total > 0
        ? money(cents(price.plan.figure * (total / price.total)))
        : price.plan.unit && price.plan.periods
            ? money(cents(total / price.plan.periods))
            : price.plan.kind === 'completion' ? perVisit : total;
    return { ...price, total, perVisit, plan: { ...price.plan, figure } };
}
/**
 * Switching a parent off takes its children with it (§7.2).
 *
 * Switching it back **on** brings the required work back and leaves the
 * optional services and lines off: nothing is added by accident. So this is
 * only ever called on the way down.
 */
export function withoutChildren(view, sel, visitId) {
    const visit = view.visits.find((one) => one.id === visitId);
    if (!visit)
        return sel;
    const services = new Set(visit.services.map((one) => one.id));
    const lines = new Set(visit.services.flatMap((one) => one.lines.map((line) => line.id)));
    return {
        ...sel,
        visits: sel.visits.filter((id) => id !== visitId),
        services: sel.services.filter((id) => !services.has(id)),
        lines: sel.lines.filter((id) => !lines.has(id)),
    };
}
/** The same rule one level down: an optional service drops its own lines. */
export function withoutServiceLines(view, sel, serviceId) {
    const service = view.visits
        .flatMap((visit) => visit.services)
        .find((one) => one.id === serviceId);
    if (!service)
        return sel;
    const lines = new Set(service.lines.map((one) => one.id));
    return {
        ...sel,
        services: sel.services.filter((id) => id !== serviceId),
        lines: sel.lines.filter((id) => !lines.has(id)),
    };
}
/**
 * The same total, said by the period instead of in one figure.
 *
 * **It is a restatement, never a second price.** `total` is
 * `selectionTotals().total` and nothing else; the periodic figures are that
 * number divided, with the last period absorbing the cent. A monthly figure
 * worked out independently would be a second answer to "what does this cost",
 * and the day the two disagreed the customer would be reading one and signing
 * the other.
 *
 * Returns `null` when the quote cannot be read by the period at all
 * (`view.month` is null) — a quote mixing monthly and weekly plans, or one
 * whose only periodic plan bills in arrears, has no honest "per month".
 *
 * **Tax follows the work it belongs to.** Tax on materials for a one-off
 * installation sits in the one-time half; a monthly figure carries tax only
 * where its own visit has taxable lines. Spreading one tax figure across both
 * halves would put sales tax on mowing.
 */
export function monthSplit(view, sel) {
    if (!view.month)
        return null;
    const byPeriod = new Map();
    for (const plan of view.plans) {
        if (!periodicEven(plan, view.month))
            continue;
        for (const visitId of plan.visitIds)
            byPeriod.set(visitId, plan.periodCount ?? 0);
    }
    const rows = [];
    const oneTimeRows = [];
    let perPeriodCents = 0;
    let oneTimeCents = 0;
    let oneTimeTaxCents = 0;
    for (const visit of view.visits) {
        if (!visitIncluded(visit, sel))
            continue;
        /*
         * Open-ended work is not in the total and so is not in the restatement
         * of it: a rental's money is an amount per period (`view.recurring`),
         * stated on its own, and a $0 one-time row for it would be the page
         * saying a rental is free.
         */
        if (visit.count.openEnded)
            continue;
        /*
         * One visit's own money and its own tax, by the same walk
         * `selectionTotals` makes — so the two cannot disagree about which
         * optional service is on.
         */
        let eachCents = 0;
        let eachTaxCents = 0;
        for (const service of visit.services) {
            if (!serviceIncluded(service, sel))
                continue;
            eachCents += cents(service.hiddenEach);
            eachTaxCents += cents(service.hiddenTaxableEach);
            for (const line of service.lines) {
                if (!lineIncluded(line, sel))
                    continue;
                eachCents += cents(line.amountEach);
                if (line.taxable)
                    eachTaxCents += cents(line.amountEach);
            }
        }
        const occurrences = Math.max(0, visit.price.occurrences);
        const mineCents = eachCents * occurrences
            + Math.round(eachTaxCents * occurrences * view.tax.rate);
        const periods = byPeriod.get(visit.id) ?? 0;
        if (periods > 0 && mineCents > 0) {
            /*
             * `each` is what the customer is told they pay this month; `inAll` is
             * the exact money, so the parts add to the total even where the
             * division does not come out evenly. The last period takes the
             * difference, which is the same rule `plannedInvoices` already applies
             * to the invoices themselves.
             */
            const each = Math.round(mineCents / periods);
            rows.push({
                visitId: visit.id,
                each: money(each),
                periods,
                inAll: money(mineCents),
            });
            perPeriodCents += each;
            continue;
        }
        if (mineCents === 0)
            continue;
        oneTimeRows.push({ visitId: visit.id, amount: money(mineCents) });
        oneTimeCents += mineCents;
        oneTimeTaxCents += Math.round(eachTaxCents * occurrences * view.tax.rate);
    }
    const counts = new Set(rows.map((row) => row.periods));
    return {
        perPeriod: money(perPeriodCents),
        fromPeriod: counts.size > 1,
        rows,
        oneTime: money(oneTimeCents),
        oneTimeRows,
        oneTimeTax: money(oneTimeTaxCents),
        total: selectionTotals(view, sel).total,
    };
}
/**
 * Is this plan one the page may state "per month" for?
 *
 * `period` and `even` with a known count, at the cadence the view already
 * settled on. `actual` is deliberately out: a plan that bills for what each
 * month held cannot say what a month costs before the month has happened, and
 * §20 keeps "about $X a month" off the page for exactly that reason — it is a
 * forecast, not a price.
 */
function periodicEven(plan, month) {
    return plan.kind === 'period'
        && plan.basis === 'even'
        && plan.periodCount !== null
        && plan.periodCount > 0
        && plan.periodUnit === month.unit
        && plan.periodEvery === month.every;
}
/**
 * A long list of identical payments, said once.
 *
 * Twelve rows reading "$450.00" tell the customer nothing eleven of them did
 * not, and on a 390px column they push the thing that *is* different — the
 * deposit, the balance — off the screen. So a run of `least` or more equal
 * payments on one plan becomes one row, and **Show all 12** opens the real
 * dates.
 *
 * **Folding never removes a payment.** The series keeps every `item`, the
 * invoices are generated from the billing plan rather than from what this
 * draws, and the builder's Billing tab goes on listing all of them — which is
 * what `plannedInvoices`' own "nothing is folded" rule is about (§1.5).
 *
 * A run is **consecutive** rows with the same non-null `seriesKey` and the
 * same amount, and the **last** row may differ by up to two cents and still
 * join: the last invoice of an evenly-divided plan absorbs the rounding, and
 * a twelfth row sitting on its own because it is a penny short would be the
 * fold failing on exactly the case it exists for. A deposit, a milestone and
 * a balance each have a null `seriesKey` and never join anything.
 */
export function foldPayments(payments, least = 5) {
    const out = [];
    let index = 0;
    while (index < payments.length) {
        const head = payments[index];
        if (head.seriesKey === null) {
            out.push({ kind: 'one', payment: head });
            index += 1;
            continue;
        }
        /* How far the run of equal amounts on this key reaches. */
        let end = index + 1;
        while (end < payments.length
            && payments[end].seriesKey === head.seriesKey
            && cents(payments[end].amount) === cents(head.amount))
            end += 1;
        /* The last one may be a cent or two out and still belong to the run. */
        const next = payments[end];
        const tail = next
            && next.seriesKey === head.seriesKey
            && Math.abs(cents(next.amount) - cents(head.amount)) <= 2;
        const stop = tail ? end + 1 : end;
        const run = payments.slice(index, stop);
        if (run.length < least) {
            for (const one of run)
                out.push({ kind: 'one', payment: one });
            index = stop;
            continue;
        }
        const inAllCents = run.reduce((sum, one) => sum + cents(one.amount), 0);
        out.push({
            kind: 'series',
            seriesKey: head.seriesKey,
            title: `${run.length} ${head.seriesNoun ?? 'payments'}`,
            each: head.amount,
            count: run.length,
            inAll: money(inAllCents),
            cadence: head.cadence,
            firstLabel: run[0].whenLabel,
            lastLabel: run[run.length - 1].whenLabel,
            estimate: run.some((one) => one.estimate),
            items: run,
        });
        index = stop;
    }
    return out;
}
// ---------------------------------------------------------------------------
// `rev` — what the page compares to know it is stale (§4.5)
// ---------------------------------------------------------------------------
/**
 * Everything `rev` is a hash **of** — the offer, canonically.
 *
 * `quotes.version` cannot do `rev`'s job: the code bumps it on every update,
 * including the `sent → viewed` that the customer's own first read performs —
 * so their page would look out of date the moment they opened it and their
 * first signature would 409.
 *
 * What is in here is what would change the offer: the live visits and their
 * kinds, groups and options, every amount and taxable flag, the resolved
 * price figures, **the timing mode and phrase** (words are part of the
 * offer), the tax rate, the terms revision and section ids, and the payments.
 * What is deliberately out: `message`, photos, `expiresOn`, `viewed_at`,
 * `status` — none of which changes what Dana would be agreeing to.
 *
 * Every list is sorted by id first, so two reads of one quote canonicalise
 * the same whatever order the rows came back in.
 *
 * **The hash itself is `proposalRev()` in `server/src/services/proposal.ts`**,
 * not here, and the split is deliberate. `docs/proposal.md` §4.5 puts both in
 * this file, but this file is bundled into the customer's browser and a
 * `node:crypto` import in it would reach Vite — while the three places that
 * compute a rev (the portal read, the `price` call and `approve`'s final
 * check) are all on the server and all go through that one function. The
 * client only ever carries the string it was given. Behaviour is unchanged;
 * the canonicalisation, which is the part with judgement in it, is still
 * written once.
 */
export function revPayload(view) {
    const byId = (rows) => [...rows].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const canonical = {
        visits: byId(view.visits).map((visit) => ({
            id: visit.id,
            kind: visit.kind,
            group: visit.groupId,
            options: [...visit.optionIds].sort(),
            count: visit.count,
            mode: visit.timing.mode,
            phrase: visit.timing.phrase,
            /*
             * What the timing *resolved to*, not only which mode was asked for
             * (PR 3). A season moving from "October to early December" to
             * "October to late December" is a different document to the person
             * reading it, and so is a visit whose dates appeared — and both can
             * happen with the mode unchanged, because the mode is a question and
             * these are its answer.
             */
            season: visit.timing.season?.label ?? null,
            dates: [...visit.timing.dates],
            detail: visit.detail,
            kinds: [...visit.kinds].sort(),
            hours: visit.showHours,
            view: visit.price.view,
            figure: visit.price.plan.figure,
            unit: visit.price.plan.unit,
            periods: visit.price.plan.periods,
            services: byId(visit.services).map((service) => ({
                id: service.id,
                optional: service.optional,
                hidden: service.hiddenEach,
                hiddenTaxable: service.hiddenTaxableEach,
                /* What the switch says it adds, which the customer reads as a price. */
                adds: service.addsPerPeriod,
                lines: byId(service.lines).map((line) => ({
                    id: line.id,
                    kind: line.kind,
                    optional: line.optional,
                    taxable: line.taxable,
                    each: line.amountEach,
                    /*
                     * The quantity a line prints is words the customer agrees to —
                     * "12 yd" and "15 yd" at one price are two offers — and Show
                     * material and fee quantities (0130) puts it on the page or takes
                     * it off, the way `hours` above follows Show crew hours.
                     */
                    qty: line.qty,
                    unit: line.unit,
                })),
            })),
        })),
        groups: byId(view.groups).map((group) => ({
            id: group.id,
            allowNone: group.allowNone,
            options: byId(group.options).map((option) => ({
                id: option.id,
                visits: [...option.visitIds].sort(),
                recommended: option.recommended,
            })),
        })),
        tax: view.tax.rate,
        terms: view.terms
            ? {
                id: view.terms.id,
                revision: view.terms.revision,
                sections: view.terms.sections.map((one) => one.id).sort(),
            }
            : null,
        payments: [...view.payments]
            .map((one) => ({ label: one.label, amount: one.amount, on: one.on }))
            .sort((a, b) => (JSON.stringify(a) < JSON.stringify(b) ? -1 : 1)),
    };
    return canonical;
}
// ---------------------------------------------------------------------------
// What the office may send (§12.2)
// ---------------------------------------------------------------------------
const kindsField = z.array(z.enum(PROPOSAL_KINDS)).max(4);
// ---------------------------------------------------------------------------
// Sign and accept (§8)
// ---------------------------------------------------------------------------
export const SIGNATURE_FONTS = ['caveat', 'homemade-apple', 'mrs-saint-delafield'];
export const EMPTY_SIGNATURE = {
    name: '',
    mode: 'typed',
    font: 'caveat',
    agreed: false,
    points: 0,
    box: { width: 0, height: 0 },
};
/** Twenty points and 40 × 15 px: below either, it is a tap rather than a name. */
export const SIGNATURE_MIN_POINTS = 20;
export const SIGNATURE_MIN_BOX = { width: 40, height: 15 };
/**
 * What is still needed before Accept can do anything (§8.2).
 *
 * **The only place that decides readiness.** The button's disabled state, the
 * "To accept, you still need to" checklist beside it and the server's own
 * validation all call this — so a page that offers Accept and a server that
 * refuses it cannot be two different opinions, which is the way this goes
 * wrong: somebody signs, waits, and is told something they could not see.
 *
 * The order is the order the card lists them, because the checklist is this
 * list rendered and a customer reads it top to bottom.
 */
export function missingForAccept(view, sel, form) {
    const out = [];
    for (const group of view.groups) {
        if (sel.choose[group.id] !== undefined)
            continue;
        /*
         * "Pick Classic or Premium" where there are two, because naming them is
         * what tells somebody *where* to look; past two it is a list rather than
         * a sentence, and the group's own name is the better pointer.
         */
        const names = group.options.map((option) => option.name);
        out.push({
            key: 'choose',
            groupId: group.id,
            text: names.length === 2
                ? `Pick ${names[0]} or ${names[1]}`
                : `Choose ${group.name.toLowerCase()}`,
        });
    }
    if (form.name.trim().length < 2)
        out.push({ key: 'name', text: 'Type your full name' });
    /*
     * A typed signature *is* the name, so it is missing exactly when the name
     * is — and listing it twice would be the checklist telling somebody to do
     * one thing in two rows. A drawn one is its own question.
     */
    if (form.mode === 'drawn') {
        const enough = form.points >= SIGNATURE_MIN_POINTS
            && form.box.width >= SIGNATURE_MIN_BOX.width
            && form.box.height >= SIGNATURE_MIN_BOX.height;
        if (!enough)
            out.push({ key: 'signature', text: 'Sign above the line' });
    }
    if (!form.agreed)
        out.push({ key: 'agree', text: 'Agree to the terms' });
    return out;
}
/**
 * The approve request (§8.3), shared so the page and the route agree.
 *
 * The old shape stays accepted for one release — `chooseSelections`,
 * `optionalAdds` and `signatureName` — so a customer with the page already
 * open can still sign. A quote is read, thought about and signed hours later;
 * a deploy in between must not be a 400 on a signature.
 */
export const proposalSelectionSchema = z.object({
    choose: z.record(z.string().uuid(), z.string().uuid().nullable()).default({}),
    visits: z.array(z.string().uuid()).max(200).default([]),
    services: z.array(z.string().uuid()).max(500).default([]),
    lines: z.array(z.string().uuid()).max(1000).default([]),
});
export const signatureSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('typed'),
        name: z.string().trim().min(2).max(120),
        font: z.enum(SIGNATURE_FONTS),
    }),
    z.object({
        kind: z.literal('drawn'),
        name: z.string().trim().min(2).max(120),
        /*
         * 400 KB of base64 is the ceiling on what may be *sent*; the route
         * decodes it and enforces 200 KB and 1200 × 400 on the image itself,
         * because a length limit on a string says nothing about what it decodes
         * to.
         */
        png: z.string().regex(/^data:image\/png;base64,/).max(400_000),
    }),
]);
// ---------------------------------------------------------------------------
// The customer's four answers — §9.4, board 11
// ---------------------------------------------------------------------------
/**
 * Why somebody declined, in their own words and in ours.
 *
 * Five reasons rather than a free box alone, because a box gets "no thanks"
 * and a list gets something the company can count. The note is optional and
 * is the customer's own sentence; neither is ever argued with — §9.4's copy
 * rule is that the declined page repeats their reason and never asks them to
 * reconsider.
 */
export const DECLINE_REASONS = [
    { value: 'price', label: 'The price is more than I planned' },
    { value: 'timing', label: 'The timing does not work' },
    { value: 'other_company', label: 'I chose someone else' },
    { value: 'not_needed', label: 'I no longer need it' },
    { value: 'other', label: 'Something else' },
];
export const declineLabel = (reason) => DECLINE_REASONS.find((one) => one.value === reason)?.label ?? reason;
export const questionSchema = z.object({
    body: z.string().trim().min(1, 'Type your question.').max(2000),
});
export const changesSchema = z.object({
    /** Visit ids, validated on the server to belong to this quote. */
    refs: z.array(z.string().uuid()).max(200).default([]),
    body: z.string().trim().min(1, 'Say what you would like changed.').max(2000),
});
export const declineSchema = z.object({
    reason: z.enum(DECLINE_REASONS.map((one) => one.value)),
    note: z.string().trim().max(1000).optional(),
});
export const reopenSchema = z.object({
    note: z.string().trim().max(1000).optional(),
});
/** The chip over a response card, and the verb the office reads. */
export const RESPONSE_LABELS = {
    question: 'Question',
    changes: 'Changes',
    decline: 'Declined',
    reopen_request: 'Reopen',
};
/**
 * The Proposal tab's rail, as a patch.
 *
 * Every field optional, because the rail saves as you type and each card
 * saves itself — a payload that had to carry all eight would make changing the
 * message depend on the detail setting being valid.
 */
export const proposalPatchSchema = z.object({
    message: z.string().max(600).optional(),
    detail: z.enum(PROPOSAL_DETAILS).optional(),
    kinds: kindsField.optional(),
    linePrices: z.boolean().optional(),
    showHours: z.boolean().optional(),
    showQuantities: z.boolean().optional(),
    priceView: z.enum(PRICE_VIEWS).optional(),
    timing: z.enum(TIMING_MODES).optional(),
    validDays: z.number().int().min(1).max(365).optional(),
});
/** The company's defaults — Settings › Customers › Proposal (§10.4). */
export const proposalDefaultsSchema = z.object({
    detail: z.enum(PROPOSAL_DETAILS),
    kinds: kindsField,
    linePrices: z.boolean(),
    showHours: z.boolean(),
    showQuantities: z.boolean(),
    priceView: z.enum(PRICE_VIEWS),
    timing: z.enum(TIMING_MODES),
    datesAllowed: z.boolean(),
    validDays: z.number().int().min(1).max(365),
    nextSteps: z.array(z.string().max(120)).max(3),
    /**
     * The sales-tax estimate (migration 0113). `null` is "we have not said",
     * and the proposal then draws no tax line at all rather than a number from
     * somewhere else — which is why it is nullable rather than defaulted to a
     * rate that would be wrong for every company but one.
     */
    taxRatePct: z.number().min(0).max(99.999).nullable(),
    taxLabel: z.string().max(80).nullable(),
    /**
     * *When the office approves for the customer* (`docs/job-stages.md` §8 C,
     * migration 0162): what the office's Approve must record beyond who and
     * when — `tenant_settings.approval_require_*`, all off. Optional here, so a
     * save that does not carry them leaves them as they were; the GET always
     * answers all three.
     */
    approvalRequireHow: z.boolean().optional(),
    approvalRequireNote: z.boolean().optional(),
    approvalRequireProof: z.boolean().optional(),
});
/** One row of the phrase library (§10.4 card 2). */
export const phraseInputSchema = z.object({
    name: z.string().trim().min(1).max(60),
    text: z.string().trim().min(1).max(160),
    isDefault: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
});
// ---------------------------------------------------------------------------
// Timing — a season said in months, never in days (§6.2)
// ---------------------------------------------------------------------------
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
/**
 * How long a recurring visit runs, in **months** — `docs/proposal.md` §6.2.
 *
 * The office's `starts_on` and `ends_on` are planning dates, and §2 rule 11
 * is that a production date is not a promise. But a customer buying weekly
 * lawn care does need to know it runs through the autumn and not the year, so
 * the season is let out **with the days taken off it**: "October to early
 * December" is true, useful, and commits nobody to the 12th.
 *
 * The qualifier is on the **end** month, because that is where the question
 * is — "does it stop at the start of December or run through it". It appears
 * on the start month only when the work begins late in it, where "October"
 * would otherwise promise three extra weeks.
 *
 * Returns `null` when there is nothing honest to say: no dates, or a range
 * that runs backwards.
 */
export function seasonLabel(startsOn, endsOn) {
    const from = parseDay(startsOn);
    const to = parseDay(endsOn);
    if (!from || !to)
        return null;
    const span = (to.year - from.year) * 12 + (to.month - from.month);
    if (span < 0)
        return null;
    /* Twelve months or more is a year, and naming two months would mislead. */
    if (span >= 11)
        return { months: [], label: 'Year-round' };
    const months = [];
    for (let at = 0; at <= span; at += 1)
        months.push(SHORT[(from.month + at) % 12]);
    if (span === 0)
        return { months, label: `in ${MONTHS[from.month]}` };
    const start = from.day >= 21 ? `late ${MONTHS[from.month]}` : MONTHS[from.month];
    const end = `${qualifier(to.day)} ${MONTHS[to.month]}`.trim();
    return {
        /* The bar is a row of month tiles; past eight it is a wall, so the label
           stands alone and the caller draws nothing. */
        months: months.length <= 8 ? months : [],
        label: `${start} to ${end}`,
    };
}
const qualifier = (day) => (day <= 10 ? 'early' : day <= 20 ? 'mid' : 'late');
/** The month a project is planned for, said as a month and nothing more. */
export function monthLabel(day) {
    const parsed = parseDay(day);
    return parsed ? MONTHS[parsed.month] : null;
}
function parseDay(value) {
    if (!value)
        return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match)
        return null;
    const month = Number(match[2]) - 1;
    if (month < 0 || month > 11)
        return null;
    return { year: Number(match[1]), month, day: Number(match[3]) };
}
//# sourceMappingURL=proposal.js.map